



import React, { useState } from 'react';
import {
  Calculator, TrendingUp, DollarSign, PieChart, BarChart2, 
  LifeBuoy, Banknote, Coins, Wallet, Landmark, UserCheck,
  Layers, Percent, Smile, BookOpen, ArrowLeft, Home
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import CalculatorSEO from '../components/CalculatorSEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';

// Visual Calculation Animation Component
const CalculationAnimation = ({ isVisible }) => {
  if (!isVisible) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md mx-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center">
            <Calculator size={24} className="text-black" />
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold text-gray-900">Calculating...</div>
            <div className="text-sm text-gray-600">Processing your inputs</div>
          </div>
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-black rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Indian number format (lakhs/crores)
// Helper: Indian number format (lakhs/crores)
const formatNumber = (num) => {
  if (num >= 1e7) return `₹${(num/1e7).toFixed(2)} Cr`;
  if (num >= 1e5) return `₹${(num/1e5).toFixed(2)} L`;
  return `₹${num.toLocaleString()}`;
};


const calculators = [
  {
    id: 'sip', 
    title: 'SIP Calculator', 
    icon: <Calculator />,
    description: 'Calculate Mutual Fund SIP returns with monthly compounding. ELSS funds qualify for Sec 80C up to ₹1.5L/year.',
    category: 'Investment',
    fields: [
      { name: 'monthly_investment', label: 'Monthly Investment (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Expected Annual Return (%)', type: 'number' },
      { name: 'years', label: 'Investment Tenure (Years)', type: 'number' }
    ],
    explanation: `
**How SIP Works:**
1. You invest a fixed amount every month
2. Each installment compounds independently 
3. Formula: FV = P × [((1+r)^n - 1) / r] × (1+r)
   Where P = monthly SIP, r = monthly rate, n = total months
4. Your investment grows through rupee cost averaging and compounding

**Calculation Method:**
- Monthly rate = Annual rate ÷ 12 ÷ 100
- Each monthly investment compounds for remaining months
- Final corpus = Sum of all compounded monthly investments
`,
    formula: ({ monthly_investment, annual_rate, years }) => {
      const P = Number(monthly_investment);
      const n = Number(years) * 12;
      const r = Number(annual_rate) / (12 * 100);
      const invested = P * n;
      
      if (r === 0) {
        return { 
          future_value: invested, 
          total_invested: invested, 
          total_return: 0, 
          tax_note: 'Equity MF: STCG @ 15% (≤1yr), LTCG @ 10% above ₹1L (>1yr)' 
        };
      }
      
      const future = Math.round(P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r)));
      const returnAmt = future - invested;
      
      return { 
        future_value: future, 
        total_invested: invested, 
        total_return: returnAmt, 
        tax_note: 'Equity MF: STCG @ 15% (≤1yr), LTCG @ 10% above ₹1L (>1yr)' 
      };
    }
  },
  {
    id: 'lumpsum', 
    title: 'Lumpsum Calculator', 
    icon: <TrendingUp />,
    description: 'Calculate future value of one-time investment with annual compounding.',
    category: 'Investment',
    fields: [
      { name: 'principal', label: 'Investment Amount (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Expected Annual Return (%)', type: 'number' },
      { name: 'years', label: 'Investment Period (Years)', type: 'number' }
    ],
    explanation: `
**How Lumpsum Investment Works:**
1. One-time investment that grows with annual compounding
2. Formula: A = P × (1 + r)^t
   Where P = principal, r = annual rate, t = years
3. No rupee cost averaging benefit, but full exposure to market growth

**Calculation Method:**
- Simple compound interest formula
- Returns compound annually
- Final amount includes principal + compounded returns
`,
    formula: ({ principal, annual_rate, years }) => {
      const P = Number(principal);
      const r = Number(annual_rate) / 100;
      const t = Number(years);
      
      const future = Math.round(P * Math.pow(1 + r, t));
      const returnAmt = future - P;
      
      return { 
        future_value: future, 
        total_invested: P, 
        total_return: returnAmt, 
        tax_note: 'Tax depends on asset class - equity, debt, or hybrid funds have different tax rates' 
      };
    }
  },
  {
    id: 'ppf', 
    title: 'PPF Calculator', 
    icon: <DollarSign />,
    description: 'Public Provident Fund calculator with 15-year lock-in and tax-free maturity. Qualifies for Sec 80C.',
    category: 'Tax Saving',
    fields: [
      { name: 'yearly_investment', label: 'Annual Investment (₹)', type: 'number' },
      { name: 'annual_rate', label: 'PPF Interest Rate (%)', type: 'number', default: 7.1 },
      { name: 'years', label: 'Investment Period (Min 15 years)', type: 'number' }
    ],
    explanation: `
**How PPF Works:**
1. Annual investment (₹500 minimum, ₹1.5L maximum)
2. Investment made at beginning of each year
3. 15-year mandatory lock-in period
4. Interest compounds annually, tax-free

**Calculation Method:**
- Each yearly contribution compounds for remaining years
- Interest calculated on year-end balance
- Formula: Sum of [Annual Investment × (1 + rate)^remaining_years]
- Current PPF rate: 7.1% (as of 2024-25)
`,
    formula: ({ yearly_investment, annual_rate, years }) => {
      const P = Number(yearly_investment);
      const n = Number(years);
      const r = Number(annual_rate) / 100;
      let future = 0;
      
      // PPF: Each annual investment compounds for remaining years
      for (let i = 0; i < n; i++) {
        future += P * Math.pow(1 + r, n - i);
      }
      
      const totalInvested = P * n;
      
      return { 
        future_value: Math.round(future), 
        total_invested: totalInvested, 
        total_return: Math.round(future - totalInvested), 
        tax_note: 'Completely tax-free on maturity. Qualifies for Sec 80C deduction up to ₹1.5L/year' 
      };
    }
  },
  {
    id: 'emi', 
    title: 'EMI Calculator', 
    icon: <PieChart />,
    description: 'Calculate equated monthly installments for loans. Home loan principal qualifies for tax benefits.',
    category: 'Loan',
    fields: [
      { name: 'principal', label: 'Loan Amount (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Interest Rate (% per annum)', type: 'number' },
      { name: 'years', label: 'Loan Tenure (Years)', type: 'number' }
    ],
    explanation: `
**How EMI Calculation Works:**
1. Fixed monthly payment that includes principal + interest
2. Formula: EMI = [P × r × (1+r)^n] / [(1+r)^n - 1]
   Where P = loan amount, r = monthly rate, n = total months
3. Early payments go mostly toward interest, later toward principal

**Calculation Method:**
- Monthly interest rate = Annual rate ÷ 12
- EMI remains constant throughout loan tenure
- Total payment = EMI × number of months
- Interest component decreases over time
`,
    formula: ({ principal, annual_rate, years }) => {
      const P = Number(principal);
      const n = Number(years) * 12;
      const r = Number(annual_rate) / (12 * 100);
      
      if (r === 0) {
        const emi = P / n;
        return { 
          future_value: P, 
          emi: Math.round(emi), 
          total_invested: P, 
          total_interest: 0, 
          tax_note: 'Home loan: Principal repayment under Sec 80C (₹1.5L), Interest under Sec 24 (₹2L)' 
        };
      }
      
      const emi = P * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
      const totalPayment = emi * n;
      
      return { 
        future_value: Math.round(totalPayment), 
        emi: Math.round(emi), 
        total_invested: P, 
        total_interest: Math.round(totalPayment - P), 
        tax_note: 'Home loan: Principal repayment under Sec 80C (₹1.5L), Interest under Sec 24 (₹2L)' 
      };
    }
  },
  {
    id: 'retirement', 
    title: 'Retirement Planning Calculator', 
    icon: <LifeBuoy />,
    description: 'Calculate retirement corpus needed based on current expenses, inflation, and post-retirement returns.',
    category: 'Planning',
    fields: [
      { name: 'current_age', label: 'Current Age', type: 'number' },
      { name: 'retirement_age', label: 'Retirement Age', type: 'number' },
      { name: 'monthly_expense', label: 'Current Monthly Expenses (₹)', type: 'number' },
      { name: 'inflation', label: 'Expected Inflation Rate (%)', type: 'number' },
      { name: 'expected_return', label: 'Post-Retirement Return (%)', type: 'number' },
      { name: 'years_post_retirement', label: 'Years After Retirement', type: 'number' }
    ],
    explanation: `
**How Retirement Planning Works:**
1. Current expenses are inflated to retirement using inflation rate
2. Corpus calculation considers post-retirement life expectancy
3. Real return = (Investment return - Inflation) for purchasing power

**Calculation Method:**
- Future expense = Current expense × (1 + inflation)^years_to_retirement
- Required corpus uses present value of annuity formula
- Considers inflation impact during retirement years
- Accounts for real returns (inflation-adjusted)
`,
    formula: ({ current_age, retirement_age, monthly_expense, inflation, expected_return, years_post_retirement }) => {
      const yearsToRetirement = Number(retirement_age) - Number(current_age);
      const currentMonthlyExp = Number(monthly_expense);
      const inflationRate = Number(inflation) / 100;
      const postReturnRate = Number(expected_return) / 100;
      const postRetirementYears = Number(years_post_retirement);
      
      // Inflate current expenses to retirement
      const expensesAtRetirement = currentMonthlyExp * Math.pow(1 + inflationRate, yearsToRetirement);
      const annualExpenseAtRetirement = expensesAtRetirement * 12;
      
      // Calculate real return (inflation-adjusted)
      const realReturn = (1 + postReturnRate) / (1 + inflationRate) - 1;
      
      let corpus;
      if (Math.abs(realReturn) < 0.0001) {
        corpus = annualExpenseAtRetirement * postRetirementYears;
      } else {
        corpus = annualExpenseAtRetirement * (1 - Math.pow(1 + realReturn, -postRetirementYears)) / realReturn;
      }
      
      return { 
        future_value: Math.round(corpus), 
        total_invested: 0, 
        total_return: 0, 
        monthly_expense_at_retirement: Math.round(expensesAtRetirement),
        tax_note: `Monthly expense at retirement: ${formatNumber(expensesAtRetirement)}. Consider tax-efficient withdrawal strategy.` 
      };
    }
  },
  {
    id: 'fd', 
    title: 'Fixed Deposit Calculator', 
    icon: <Banknote />,
    description: 'Calculate FD maturity amount with compound interest. TDS applicable if annual interest exceeds ₹40,000.',
    category: 'Investment',
    fields: [
      { name: 'principal', label: 'Deposit Amount (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Interest Rate (% per annum)', type: 'number' },
      { name: 'years', label: 'Tenure (Years)', type: 'number' },
      { name: 'compound_frequency', label: 'Compounding (1=Annual, 4=Quarterly)', type: 'number', default: 4 }
    ],
    explanation: `
**How FD Works:**
1. Fixed interest rate guaranteed by bank
2. Interest compounds at specified frequency (usually quarterly)
3. Formula: A = P × (1 + r/n)^(n×t)
   Where n = compounding frequency per year

**Calculation Method:**
- Quarterly compounding is most common
- TDS @ 10% if annual interest > ₹40,000
- Interest is fully taxable as per income tax slab
- No capital gains tax applicable
`,
    formula: ({ principal, annual_rate, years, compound_frequency }) => {
      const P = Number(principal);
      const r = Number(annual_rate) / 100;
      const t = Number(years);
      const n = Number(compound_frequency) || 4;
      
      const maturityAmount = P * Math.pow(1 + r / n, n * t);
      const interest = maturityAmount - P;
      
      // TDS calculation
      const annualInterest = interest / t;
      const tds = annualInterest > 40000 ? interest * 0.10 : 0;
      
      return { 
        future_value: Math.round(maturityAmount), 
        total_invested: P, 
        total_return: Math.round(interest), 
        tds: Math.round(tds), 
        tax_note: tds > 0 ? 
          `TDS of ${formatNumber(tds)} will be deducted. Interest is taxable as per your income slab.` : 
          'Interest is taxable as per income slab. No TDS as annual interest < ₹40,000.' 
      };
    }
  },
  {
    id: 'rd', 
    title: 'Recurring Deposit Calculator', 
    icon: <Coins />,
    description: 'Calculate RD maturity with monthly deposits and compound interest. Interest taxable as per income slab.',
    category: 'Investment',
    fields: [
      { name: 'monthly_investment', label: 'Monthly Deposit (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Interest Rate (% per annum)', type: 'number' },
      { name: 'years', label: 'Tenure (Years)', type: 'number' }
    ],
    explanation: `
**How RD Works:**
1. Fixed monthly deposits for specified tenure
2. Each installment earns interest from deposit date to maturity
3. Monthly compounding on each installment separately
4. Formula: Each monthly deposit compounds for remaining months

**Calculation Method :**
- True monthly rate = (1 + annual_rate)^(1/12) - 1
- Each deposit compounds independently for remaining tenure
- Maturity = Sum of [Monthly_deposit × (1 + monthly_rate)^months_remaining]
`,
    formula: ({ monthly_investment, annual_rate, years }) => {
      const P = Number(monthly_investment);
      const totalMonths = Number(years) * 12;
      const annualRate = Number(annual_rate) / 100;
      const totalInvested = P * totalMonths;
      
      if (annualRate === 0) {
        return { 
          future_value: totalInvested, 
          total_invested: totalInvested, 
          total_return: 0, 
          tax_note: 'Interest is taxable as per income slab. No Section 80C benefit.' 
        };
      }
      
      // Groww's true monthly compounding rate
      const monthlyRate = Math.pow(1 + annualRate, 1/12) - 1;
      
      let maturity = 0;
      for (let i = 0; i < totalMonths; i++) {
        const monthsRemaining = totalMonths - i;
        maturity += P * Math.pow(1 + monthlyRate, monthsRemaining);
      }
      
      return { 
        future_value: Math.round(maturity), 
        total_invested: totalInvested, 
        total_return: Math.round(maturity - totalInvested), 
        tax_note: 'Interest is taxable as per income slab. No Section 80C benefit available.' 
      };
    }
  },
  {
    id: 'nps', 
    title: 'NPS Calculator', 
    icon: <Wallet />,
    description: 'National Pension System calculator. Extra ₹50K tax deduction under Sec 80CCD(1B) over 80C limit.',
    category: 'Tax Saving',
    fields: [
      { name: 'monthly_investment', label: 'Monthly Contribution (₹)', type: 'number' },
      { name: 'years', label: 'Investment Period (Years)', type: 'number' },
      { name: 'annual_rate', label: 'Expected Annual Return (%)', type: 'number' }
    ],
    explanation: `
**How NPS Works:**
1. Monthly contributions compound until retirement (age 60)
2. At maturity: 60% can be withdrawn tax-free, 40% must buy annuity
3. Additional ₹50K deduction under Sec 80CCD(1B)
4. Market-linked returns based on fund choice

**Calculation Method:**
- Uses SIP formula for corpus calculation
- Tax-free portion = 60% of corpus
- Mandatory annuity = 40% of corpus (taxable on receipt)
- Government co-contribution may apply for certain categories
`,
    formula: ({ monthly_investment, annual_rate, years }) => {
      const P = Number(monthly_investment);
      const n = Number(years) * 12;
      const r = Number(annual_rate) / (12 * 100);
      const invested = P * n;
      
      if (r === 0) {
        return { 
          future_value: invested, 
          total_invested: invested, 
          total_return: 0, 
          tax_note: '60% corpus tax-free withdrawal, 40% for annuity (taxable). Extra ₹50K deduction under 80CCD(1B)' 
        };
      }
      
      const corpus = Math.round(P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r)));
      const taxFreeWithdrawal = Math.round(corpus * 0.6);
      const mandatoryAnnuity = corpus - taxFreeWithdrawal;
      
      return { 
        future_value: corpus, 
        total_invested: invested, 
        total_return: corpus - invested,
        tax_free_withdrawal: taxFreeWithdrawal,
        annuity_amount: mandatoryAnnuity,
        tax_note: `Tax-free withdrawal: ${formatNumber(taxFreeWithdrawal)}. Annuity: ${formatNumber(mandatoryAnnuity)} (taxable). Extra ₹50K deduction available.` 
      };
    }
  },
  {
    id: 'elss', 
    title: 'ELSS Tax Saver Calculator', 
    icon: <BarChart2 />,
    description: 'Equity Linked Savings Scheme with 3-year lock-in. Tax deduction up to ₹1.5L under Section 80C.',
    category: 'Tax Saving',
    fields: [
      { name: 'investment', label: 'Annual Investment (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Expected Annual Return (%)', type: 'number' },
      { name: 'years', label: 'Investment Period (Years)', type: 'number' }
    ],
    explanation: `
**How ELSS Works:**
1. Equity mutual funds with 3-year mandatory lock-in
2. Each annual investment has separate 3-year lock-in period
3. Tax deduction up to ₹1.5L per year under Section 80C
4. LTCG tax applicable after 3 years (₹1L exempt, then 10%)

**Calculation Method:**
- Each year's investment compounds for remaining investment period
- No exit load after 3 years
- Equity taxation: LTCG @ 10% above ₹1L exemption
- Shortest lock-in period among all 80C investments
`,
    formula: ({ investment, annual_rate, years }) => {
      const P = Number(investment);
      const r = Number(annual_rate) / 100;
      const n = Number(years);
      let futureValue = 0;
      
      // Each annual investment compounds for remaining years 
      for (let i = 0; i < n; i++) {
        const yearsToGrow = n - i;
        futureValue += P * Math.pow(1 + r, yearsToGrow);
      }
      
      const totalInvested = P * n;
      
      return { 
        future_value: Math.round(futureValue), 
        total_invested: totalInvested, 
        total_return: Math.round(futureValue - totalInvested), 
        tax_note: 'Tax deduction up to ₹1.5L/year under 80C. LTCG tax @ 10% above ₹1L exemption after 3 years.' 
      };
    }
  },
  {
    id: 'gratuity', 
    title: 'Gratuity Calculator', 
    icon: <Landmark />,
    description: 'Calculate gratuity as per Payment of Gratuity Act. Tax-free up to ₹20 lakh for private employees.',
    category: 'Salary',
    fields: [
      { name: 'last_drawn_salary', label: 'Last Drawn Basic + DA (₹/month)', type: 'number' },
      { name: 'years_of_service', label: 'Completed Years of Service', type: 'number' }
    ],
    explanation: `
**How Gratuity is Calculated:**
1. Applicable after 5 years of continuous service
2. Formula: (Basic + DA) × 15/26 × Completed years of service
3. 15 days salary for each completed year
4. Based on last drawn salary (Basic + Dearness Allowance)

**Tax Treatment:**
- Tax-free up to ₹20 lakh for private sector employees
- Government employees have different exemption limits
- Excess amount over exemption limit is taxable
- No TDS deduction by employer on exempt portion
`,
    formula: ({ last_drawn_salary, years_of_service }) => {
      const monthlySalary = Number(last_drawn_salary);
      const completedYears = Math.floor(Number(years_of_service));
      
      // Gratuity formula as per Payment of Gratuity Act
      const gratuityAmount = (monthlySalary * 15 * completedYears) / 26;
      const exemptionLimit = 2000000; // ₹20 lakh
      
      const taxFreeAmount = Math.min(gratuityAmount, exemptionLimit);
      const taxableAmount = Math.max(0, gratuityAmount - exemptionLimit);
      
      return { 
        future_value: Math.round(gratuityAmount), 
        total_invested: 0, 
        total_return: Math.round(gratuityAmount),
        tax_free_amount: Math.round(taxFreeAmount),
        taxable_amount: Math.round(taxableAmount),
        tax_note: `Tax-free: ${formatNumber(taxFreeAmount)}. ${taxableAmount > 0 ? `Taxable excess: ${formatNumber(taxableAmount)}` : 'Fully exempt from tax.'}` 
      };
    }
  },
  {
    id: 'hra', 
    title: 'HRA Exemption Calculator', 
    icon: <UserCheck />,
    description: 'Calculate House Rent Allowance tax exemption under Section 10(13A) for salaried employees.',
    category: 'Salary',
    fields: [
      { name: 'basic_salary', label: 'Annual Basic Salary (₹)', type: 'number' },
      { name: 'hra_received', label: 'Annual HRA Received (₹)', type: 'number' },
      { name: 'rent_paid', label: 'Annual Rent Paid (₹)', type: 'number' },
      { name: 'metro', label: 'Metro City? (1=Yes, 0=No)', type: 'number', default: 0 }
    ],
    explanation: `
**HRA Exemption Rules:**
1. Exemption = Minimum of three amounts:
   - Actual HRA received
   - Rent paid minus 10% of basic salary
   - 50% of basic salary (metro) or 40% (non-metro)

**Metro Cities:** Delhi, Mumbai, Chennai, Kolkata

**Calculation Method:**
- Only applicable if you receive HRA and pay rent
- Rent receipts required for claims above ₹1 lakh/year
- Balance HRA amount is fully taxable
- No exemption if living in own house
`,
    formula: ({ basic_salary, hra_received, rent_paid, metro }) => {
      const basicSalary = Number(basic_salary);
      const hraReceived = Number(hra_received);
      const rentPaid = Number(rent_paid);
      const isMetro = Number(metro) === 1;
      
      // Three conditions for HRA exemption
      const actualHRA = hraReceived;
      const rentMinusBasic = Math.max(0, rentPaid - (0.1 * basicSalary));
      const percentageBasic = basicSalary * (isMetro ? 0.5 : 0.4);
      
      const exemptAmount = Math.min(actualHRA, rentMinusBasic, percentageBasic);
      const taxableHRA = hraReceived - exemptAmount;
      
      return { 
        future_value: Math.round(exemptAmount), 
        total_invested: 0, 
        total_return: 0,
        taxable_hra: Math.round(taxableHRA),
        tax_note: `Exempt: ${formatNumber(exemptAmount)}. Taxable: ${formatNumber(taxableHRA)}. Exemption = Min(HRA received, Rent - 10% basic, ${isMetro ? '50%' : '40%'} of basic salary)` 
      };
    }
  },
  {
    id: 'capitalgains', 
    title: 'Capital Gains Tax Calculator', 
    icon: <Layers />,
    description: 'Calculate tax on sale of equity, debt, gold and property as per latest tax rates and holding periods.',
    category: 'Tax',
    fields: [
      { name: 'asset_type', label: 'Asset Type (equity/debt/gold/property)', type: 'text' },
      { name: 'holding_period_months', label: 'Holding Period (Months)', type: 'number' },
      { name: 'purchase_price', label: 'Purchase Price (₹)', type: 'number' },
      { name: 'sale_price', label: 'Sale Price (₹)', type: 'number' },
      { name: 'indexed_cost', label: 'Indexed Cost (₹) - For LTCG with indexation', type: 'number', default: 0 }
    ],
    explanation: `
**Capital Gains Tax Rules:**

**Equity Shares/Equity MF:**
- Short Term (≤12 months): 15% tax
- Long Term (>12 months): 10% tax above ₹1 lakh exemption per year

**Debt MF/Bonds/Gold:**
- Short Term (≤36 months): As per income tax slab
- Long Term (>36 months): 20% with indexation benefit

**Property:**
- Short Term (≤24 months): As per income tax slab  
- Long Term (>24 months): 20% with indexation benefit

**Calculation considers holding period and asset type for accurate tax computation.**
`,
    formula: ({ asset_type, holding_period_months, purchase_price, sale_price, indexed_cost }) => {
      const assetType = (asset_type || '').toLowerCase().trim();
      const holdingMonths = Number(holding_period_months);
      const costPrice = Number(purchase_price);
      const sellPrice = Number(sale_price);
      const indexedCost = Number(indexed_cost) || costPrice;
      
      const totalGain = sellPrice - costPrice;
      let stcgTax = 0, ltcgTax = 0, taxNote = '';
      
      if (assetType === 'equity') {
        if (holdingMonths > 12) {
          ltcgTax = Math.max(0, totalGain - 100000) * 0.10;
          taxNote = 'Long Term (>12 months): ₹1 lakh exempt per financial year, excess taxed @ 10%';
        } else {
          stcgTax = Math.max(0, totalGain) * 0.15;
          taxNote = 'Short Term (≤12 months): 15% tax on gains';
        }
      } else if (assetType === 'debt') {
        if (holdingMonths > 36) {
          const indexedGain = sellPrice - indexedCost;
          ltcgTax = Math.max(0, indexedGain) * 0.20;
          taxNote = 'Long Term (>36 months): 20% tax with indexation benefit';
        } else {
          stcgTax = Math.max(0, totalGain) * 0.30; // Assuming 30% slab
          taxNote = 'Short Term (≤36 months): Taxed as per income tax slab rate';
        }
      } else if (assetType === 'gold') {
        if (holdingMonths > 36) {
          const indexedGain = sellPrice - indexedCost;
          ltcgTax = Math.max(0, indexedGain) * 0.20;
          taxNote = 'Long Term (>36 months): 20% tax with indexation benefit';
        } else {
          stcgTax = Math.max(0, totalGain) * 0.30; // Assuming 30% slab
          taxNote = 'Short Term (≤36 months): Taxed as per income tax slab rate';
        }
      } else if (assetType === 'property') {
        if (holdingMonths > 24) {
          const indexedGain = sellPrice - indexedCost;
          ltcgTax = Math.max(0, indexedGain) * 0.20;
          taxNote = 'Long Term (>24 months): 20% tax with indexation benefit';
        } else {
          stcgTax = Math.max(0, totalGain) * 0.30; // Assuming 30% slab
          taxNote = 'Short Term (≤24 months): Taxed as per income tax slab rate';
        }
      } else {
        taxNote = 'Please specify valid asset type: equity, debt, gold, or property';
      }
      
      const totalTax = stcgTax + ltcgTax;
      const netGain = totalGain - totalTax;
      
      return { 
        future_value: sellPrice, 
        total_invested: costPrice, 
        total_return: totalGain,
        stcg_tax: Math.round(stcgTax),
        ltcg_tax: Math.round(ltcgTax),
        total_tax: Math.round(totalTax),
        net_gain: Math.round(netGain),
        tax_note: taxNote 
      };
    }
  },
  {
    id: 'ctc', 
    title: 'Salary Calculator (CTC to Take-home)', 
    icon: <Percent />,
    description: 'Calculate in-hand salary from CTC after all deductions and taxes (New Tax Regime FY 2024-25).',
    category: 'Salary',
    fields: [
      { name: 'annual_ctc', label: 'Annual CTC (₹)', type: 'number' },
      { name: 'other_deductions', label: 'Other Deductions (₹/year)', type: 'number', default: 0 }
    ],
    explanation: `
**CTC Breakdown (Typical):**
- Basic Salary: 40% of CTC
- HRA: 20% of CTC  
- Other Allowances: 40% of CTC

**Deductions:**
- EPF: 12% of basic salary (employee contribution)
- Professional Tax: ₹2,400/year (varies by state)
- Standard Deduction: ₹50,000

**Tax Calculation (New Regime FY 2024-25):**
- 0% up to ₹3L, 5% on ₹3-7L, 10% on ₹7-10L, 
- 15% on ₹10-12L, 20% on ₹12-15L, 30% above ₹15L
- Health & Education Cess: 4% of tax amount
`,
    formula: ({ annual_ctc, other_deductions }) => {
      const ctc = Number(annual_ctc);
      const otherDed = Number(other_deductions) || 0;
      
      // CTC breakdown
      const basicSalary = ctc * 0.4;
      const hra = ctc * 0.2;
      const allowances = ctc * 0.4;
      
      // Statutory deductions
      const epfEmployee = Math.min(basicSalary * 0.12, 21600); // Capped at ₹1.8L basic
      const professionalTax = 2400; // Annual PT
      const standardDeduction = 50000;
      
      // Taxable income
      const grossSalary = ctc;
      const taxableIncome = Math.max(0, grossSalary - epfEmployee - standardDeduction);
      
      // New tax regime calculation (FY 2024-25)
      let incomeTax = 0;
      const taxSlabs = [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 700000, rate: 0.05 },
        { min: 700000, max: 1000000, rate: 0.10 },
        { min: 1000000, max: 1200000, rate: 0.15 },
        { min: 1200000, max: 1500000, rate: 0.20 },
        { min: 1500000, max: Infinity, rate: 0.30 }
      ];
      
      for (const slab of taxSlabs) {
        if (taxableIncome > slab.min) {
          const taxableInThisSlab = Math.min(slab.max, taxableIncome) - slab.min;
          incomeTax += taxableInThisSlab * slab.rate;
        }
      }
      
      // Health and Education Cess
      const cess = incomeTax * 0.04;
      const totalTax = incomeTax + cess;
      
      // Net salary
      const netAnnualSalary = ctc - epfEmployee - totalTax - professionalTax - otherDed;
      const netMonthlySalary = netAnnualSalary / 12;
      
      return { 
        future_value: Math.round(netAnnualSalary), 
        total_invested: ctc, 
        total_return: Math.round(netAnnualSalary),
        monthly_salary: Math.round(netMonthlySalary),
        income_tax: Math.round(totalTax),
        pf_deduction: Math.round(epfEmployee),
        professional_tax: professionalTax,
        tax_note: `Monthly in-hand: ${formatNumber(netMonthlySalary)}. Annual tax: ${formatNumber(totalTax)}. EPF: ${formatNumber(epfEmployee)}. PT: ${formatNumber(professionalTax)}` 
      };
    }
  },
  {
    id: 'swp', 
    title: 'SWP (Systematic Withdrawal Plan)', 
    icon: <Smile />,
    description: 'Calculate how long your lump sum investment will last with regular monthly withdrawals.',
    category: 'Investment',
    fields: [
      { name: 'initial_amount', label: 'Initial Investment (₹)', type: 'number' },
      { name: 'annual_rate', label: 'Expected Annual Return (%)', type: 'number' },
      { name: 'monthly_withdrawal', label: 'Monthly Withdrawal (₹)', type: 'number' },
      { name: 'years', label: 'Planned Duration (Years)', type: 'number' }
    ],
    explanation: `
**How SWP Works :**
1. Lump sum investment grows monthly with compound returns
2. Fixed withdrawal amount is deducted each month
3. Remaining balance continues to compound
4. Process continues until corpus is exhausted or target duration reached

**Calculation Method:**
- Monthly growth rate = (1 + annual_rate)^(1/12) - 1
- Each month: Balance grows first, then withdrawal is deducted
- Stops when balance < withdrawal amount
- Shows exact duration your money will last
`,
    formula: ({ initial_amount, annual_rate, monthly_withdrawal, years }) => {
      const initialInvestment = Number(initial_amount);
      const annualReturn = Number(annual_rate) / 100;
      const monthlyReturn = Math.pow(1 + annualReturn, 1/12) - 1; // Groww's true monthly rate
      const monthlyWithdrawal = Number(monthly_withdrawal);
      const maxMonths = Number(years) * 12;
      
      let remainingBalance = initialInvestment;
      let totalWithdrawn = 0;
      let monthsLasted = 0;
      
      for (let month = 1; month <= maxMonths; month++) {
        // Step 1: Apply monthly growth
        remainingBalance *= (1 + monthlyReturn);
        
        // Step 2: Check if sufficient balance for withdrawal
        if (remainingBalance >= monthlyWithdrawal) {
          remainingBalance -= monthlyWithdrawal;
          totalWithdrawn += monthlyWithdrawal;
          monthsLasted = month;
        } else {
          // Final partial withdrawal
          totalWithdrawn += remainingBalance;
          remainingBalance = 0;
          monthsLasted = month;
          break;
        }
        
        if (remainingBalance <= 0) break;
      }
      
      const yearsLasted = Math.floor(monthsLasted / 12);
      const extraMonths = monthsLasted % 12;
      const totalReturn = totalWithdrawn - initialInvestment;
      
      return { 
        future_value: Math.round(remainingBalance), 
        total_invested: initialInvestment, 
        total_return: Math.round(totalReturn),
        total_withdrawn: Math.round(totalWithdrawn),
        months_lasted: monthsLasted,
        tax_note: `Corpus will last ${yearsLasted} years ${extraMonths} months. Total withdrawn: ${formatNumber(totalWithdrawn)}. Capital gains tax may apply on each withdrawal.` 
      };
    }
  },
  {
    id: 'step_up_sip', 
    title: 'Step-up SIP Calculator', 
    icon: <TrendingUp />,
    description: 'SIP calculator with annual increase in investment amount to beat inflation and increase wealth faster.',
    category: 'Investment',
    fields: [
      { name: 'initial_monthly_investment', label: 'Starting Monthly SIP (₹)', type: 'number' },
      { name: 'annual_increase', label: 'Annual Increase (%)', type: 'number' },
      { name: 'annual_rate', label: 'Expected Annual Return (%)', type: 'number' },
      { name: 'years', label: 'Investment Period (Years)', type: 'number' }
    ],
    explanation: `
**How Step-up SIP Works:**
1. Start with initial monthly SIP amount
2. Increase SIP by fixed percentage every year
3. Higher contributions in later years create wealth acceleration
4. Helps counter inflation and salary increases

**Calculation Method:**
- Year 1: Monthly SIP = Initial amount
- Year 2: Monthly SIP = Initial × (1 + increase%)
- Each month's contribution compounds for remaining months
- Final corpus = Sum of all monthly contributions with their respective growth periods

**Benefits:**
- Beats inflation automatically
- Utilizes salary increases effectively
- Accelerated wealth creation compared to fixed SIP
`,
    formula: ({ initial_monthly_investment, annual_increase, annual_rate, years }) => {
      const initialMonthly = Number(initial_monthly_investment);
      const stepUpRate = Number(annual_increase) / 100;
      const annualReturn = Number(annual_rate) / 100;
      const monthlyReturn = Math.pow(1 + annualReturn, 1/12) - 1; // Groww's true monthly rate
      const totalYears = Number(years);
      
      let futureValue = 0;
      let totalInvested = 0;
      let currentYearlyInvestment = initialMonthly;
      
      for (let year = 1; year <= totalYears; year++) {
        // Calculate for 12 months of current year
        for (let month = 1; month <= 12; month++) {
          const totalMonthsFromStart = (year - 1) * 12 + month;
          const monthsRemaining = (totalYears * 12) - totalMonthsFromStart + 1;
          
          const monthlyFV = currentYearlyInvestment * Math.pow(1 + monthlyReturn, monthsRemaining);
          futureValue += monthlyFV;
          totalInvested += currentYearlyInvestment;
        }
        
        // Increase investment for next year
        if (year < totalYears) {
          currentYearlyInvestment *= (1 + stepUpRate);
        }
      }
      
      const totalReturns = futureValue - totalInvested;
      const finalMonthlySIP = currentYearlyInvestment;
      
      return { 
        future_value: Math.round(futureValue), 
        total_invested: Math.round(totalInvested), 
        total_return: Math.round(totalReturns),
        final_monthly_investment: Math.round(finalMonthlySIP),
        tax_note: `Final monthly SIP: ${formatNumber(finalMonthlySIP)}. Step-up helps beat inflation. Equity MF: LTCG @ 10% above ₹1L exemption.` 
      };
    }
  }
];


// Get unique categories
const categories = [...new Set(calculators.map(calc => calc.category))];

// =========== Main CalculatorPage component ================
const CalculatorPage = () => {
  const [open, setOpen] = useState(false);
  const [selectedCalculator, setSelectedCalculator] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [result, setResult] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isCalculating, setIsCalculating] = useState(false);

  // Category filter
  const filteredCalculators = selectedCategory === 'All' 
    ? calculators 
    : calculators.filter(calc => calc.category === selectedCategory);

  // Input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (formErrors[name]) {
      const newErrors = { ...formErrors };
      delete newErrors[name];
      setFormErrors(newErrors);
    }
  };

  const handleOpen = (calculator) => {
    setSelectedCalculator(calculator);
    setFormData({});
    setResult(null);
    setFormErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCalculator(null);
    setResult(null);
    setFormData({});
    setFormErrors({});
  };

  // Main calculation logic
  const handleSubmit = async () => {
    const errors = {};
    if (selectedCalculator) {
      selectedCalculator.fields.forEach(field => {
        if (
          (!formData[field.name] && !field.default) ||
          (formData[field.name] && isNaN(Number(formData[field.name])))
        ) {
          errors[field.name] = 'Required';
        }
      });
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsCalculating(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    let args = {};
    selectedCalculator.fields.forEach(field => {
      args[field.name] = formData[field.name] || field.default || '';
    });
    try {
      const resultData = selectedCalculator.formula(args);
      setResult(resultData);
    } catch (err) {
      setResult({ error: true, tax_note: 'Error in calculation, input values.' })
    } finally {
      setIsCalculating(false);
    }
  };

  // Render input fields
  const renderFields = () => {
    if (!selectedCalculator) return null;
    return selectedCalculator.fields.map(field => (
      <div key={field.name} className="space-y-2 ">
        <Label htmlFor={field.name} className="text-sm font-medium text-gray-700">
          {field.label}
        </Label>
        <Input
          type="text"
          id={field.name}
          name={field.name}
          value={formData[field.name] || ''}
          onChange={handleChange}
          className={`${!!formErrors[field.name] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          placeholder={field.label}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.currentTarget.select()}
        />
        {!!formErrors[field.name] && (
          <div className="text-sm text-red-600">{formErrors[field.name]}</div>
        )}
        {field.tooltip && (
          <div className="text-xs text-gray-500">{field.tooltip}</div>
        )}
      </div>
    ));
  };

  // Render result section
  const renderResult = () => {
    if (!result) return null;
    // Special "books" case
    if(selectedCalculator.id==="books"){
      return (
        <pre className="bg-gray-50 text-gray-700 rounded-lg p-5 text-sm font-mono border">
          {result.tax_note}
        </pre>
      );
    }
    if(result.error) return (
      <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">{result.tax_note}</div>
    );
         return (
       <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
         <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
           <div className="w-10 h-10 bg-black/10 rounded-lg flex items-center justify-center">
             <Calculator size={20} className="text-black" />
           </div>
           <h3 className="text-xl font-semibold text-gray-900">Calculation Results</h3>
         </div>
         
         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
           {result.total_invested !== undefined && (
             <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
               <p className="text-sm font-medium text-gray-600 mb-1">Total Invested</p>
               <p className="text-lg font-semibold text-gray-900">{formatNumber(result.total_invested)}</p>
             </div>
           )}
           
           <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
             <p className="text-sm font-medium text-gray-600 mb-1">Maturity Value / Net Amount</p>
             <p className="text-lg font-semibold text-gray-900">{formatNumber(result.future_value)}</p>
           </div>
           
           {result.total_return !== undefined && (
             <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
               <p className="text-sm font-medium text-gray-600 mb-1">Total Returns</p>
               <p className={`text-lg font-semibold ${Math.abs(result.total_return) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                 {formatNumber(Math.abs(result.total_return))}
               </p>
             </div>
           )}
           
           {result.emi && (
             <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
               <p className="text-sm font-medium text-gray-600 mb-1">Monthly EMI</p>
               <p className="text-lg font-semibold text-gray-900">{formatNumber(result.emi)}</p>
             </div>
           )}
           
           {result.tds !== undefined && (
             <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
               <p className="text-sm font-medium text-gray-600 mb-1">TDS (approx)</p>
               <p className="text-lg font-semibold text-gray-900">{formatNumber(result.tds)}</p>
             </div>
           )}
           
           {result.ltcg !== undefined && (
             <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
               <p className="text-sm font-medium text-gray-600 mb-1">LTCG Tax</p>
               <p className="text-lg font-semibold text-gray-900">{formatNumber(result.ltcg)}</p>
             </div>
           )}
           
           {result.stcg !== undefined && (
             <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
               <p className="text-sm font-medium text-gray-600 mb-1">STCG Tax</p>
               <p className="text-lg font-semibold text-gray-900">{formatNumber(result.stcg)}</p>
             </div>
           )}
         </div>
         
         <div className="bg-black/5 rounded-lg p-4 border border-black/10">
           <h4 className="text-lg font-semibold text-black-900 mb-3">Calculation Explanation</h4>
           <div className="text-sm text-black-800 prose prose-sm max-w-none">
             {selectedCalculator.explanation ? (
               <div dangerouslySetInnerHTML={{ __html: selectedCalculator.explanation.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
             ) : (
               <div>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'sip' && 'Future Value = P × [(1+r)ⁿ - 1] / r × (1+r)'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'lumpsum' && 'Future Value = Principal × (1 + Rate)ⁿ'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'emi' && 'EMI = P × r × (1+r)ⁿ / [(1+r)ⁿ - 1]'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'fd' && 'Maturity Amount = Principal × (1 + Rate)ⁿ'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'ppf' && 'Maturity Amount = Annual Contribution × [(1+r)ⁿ - 1] / r'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'nps' && 'Future Value = P × [(1+r)ⁿ - 1] / r × (1+r)'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'ltcg' && 'Tax = (Sale Value - Purchase Value) × 10%'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'stcg' && 'Tax = (Sale Value - Purchase Value) × 15%'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'withdrawal' && 'Monthly withdrawal from corpus with interest'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'retirement' && 'Corpus = PV of Annuity'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'rd' && 'Quarterly compounding interest'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'elss' && 'Future Value of annual investments'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'gratuity' && 'Gratuity = Last Salary × 15/26 × Years of Service'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'hra' && 'Least of three amounts'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'capitalgains' && 'Varies by asset type and holding period'}</p>
                 <p><strong>Formula:</strong> {selectedCalculator.id === 'ctc' && 'Net Salary = Gross - Tax - Deductions'}</p>
                 <p>This calculator uses standard financial formulas to compute the results based on your inputs.</p>
               </div>
             )}
           </div>
         </div>
         
         <div className="bg-black/5 rounded-lg p-4 border border-black/10">
           <p className="text-sm text-black-800">{result.tax_note}</p>
         </div>
         
         <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
           <p className="text-xs text-gray-600">
             <strong>Disclaimer:</strong> The calculations shown are approximate and for educational purposes only. 
             Actual results may vary due to market fluctuations, changes in interest rates, tax laws, and other factors. 
             Please consult with a financial advisor before making investment decisions.
           </p>
         </div>
       </div>
     );
  };

  return (
    <>
      <CalculatorSEO />
      <Nav />
      <div className="min-h-screen bg-gray-50 mt-20">
        {/* Calculation Animation Overlay */}
        <CalculationAnimation isVisible={isCalculating} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              India Finance Toolkit
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive financial calculators for Indian investors and taxpayers
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button 
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                selectedCategory === 'All' 
                  ? 'bg-black text-white shadow-lg' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => setSelectedCategory('All')}
            >
              All Calculators
            </button>
            {categories.map(category => (
              <button 
                key={category}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category 
                    ? 'bg-black text-white shadow-lg' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCalculators.map(calculator => (
              <div 
                key={calculator.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 overflow-hidden group"
                onClick={() => handleOpen(calculator)}
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-black/10 rounded-lg flex items-center justify-center text-black group-hover:bg-black/20 transition-colors">
                      {calculator.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-black transition-colors">
                        {calculator.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                        {calculator.description}
                      </p>
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black/10 text-black-800">
                        {calculator.category}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Dialog 
            open={open} 
            onOpenChange={setOpen}
          >
            {selectedCalculator && (
              <>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-[9999]">
                  <DialogHeader>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-black/10 rounded-lg flex items-center justify-center text-black">
                        {selectedCalculator.icon}
                      </div>
                      <div className="flex-1">
                        <DialogTitle className="text-2xl font-bold text-gray-900">
                          {selectedCalculator.title}
                        </DialogTitle>
                        <DialogDescription className="text-gray-600 mt-2">
                          {selectedCalculator.description}
                        </DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>
                    
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Input Parameters
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {renderFields()}
                      </div>
                    </div>
                    {renderResult()}
                  </div>
                    
                  <DialogFooter className="flex space-x-3 pt-6 border-t border-gray-200">
                    <Button onClick={handleClose} variant="outline">
                      Close
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={isCalculating}
                      className="min-w-[140px]"
                    >
                      {isCalculating ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Calculating...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Calculator size={16} />
                          <span>Calculate</span>
                        </div>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </>
            )}
          </Dialog>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CalculatorPage;